/**
 * Auto-join from a friend's invite link. A shared `goodtime.casino/r/GTC-XXXX`
 * sends a web visitor to `play.goodtime.casino/?join=GTC-XXXX`; on launch the app
 * reads that code and drops them straight into the friend's table (via the
 * existing {@code joinWheelRoomByCode}), once the player exists and is entitled.
 *
 * <p>Mirrors the referral capture in {@link ./api/invites} but for the room
 * invite, with one extra wrinkle: a brand-new invitee hits the paywall first and
 * subscribes, which navigates out to Stripe and back. So the code is stashed in
 * localStorage (same play.goodtime.casino origin → survives that round-trip) with
 * a short TTL — a table invite is ephemeral, so we don't want a day-old code
 * auto-joining a room that's long gone.
 */

const PENDING_KEY = 'goodtime.join.pending';
const TTL_MS = 2 * 60 * 60 * 1000; // 2h — a private table won't outlive this

interface Pending {
  code: string;
  ts: number;
}

function stash(code: string): void {
  try {
    localStorage.setItem(PENDING_KEY, JSON.stringify({ code, ts: Date.now() } satisfies Pending));
  } catch {
    /* private mode — the URL param still covers the no-round-trip case */
  }
}

/**
 * Read a table join code at launch — from `?join=CODE` or a `/r/CODE` path —
 * strip it from the address bar so a refresh can't re-trigger, and stash it so
 * it survives the paywall → Stripe → back-to-web-play hop (which drops the query
 * string). Falls back to a still-valid stashed code when the URL has none.
 * Returns the upper-cased code, or null. Call once at startup.
 */
export function readLaunchJoinCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const u = new URL(window.location.href);
    let fromUrl = u.searchParams.get('join');
    if (!fromUrl) {
      const m = u.pathname.match(/\/r\/([A-Za-z0-9-]+)/);
      fromUrl = m?.[1] ?? null;
    }
    if (fromUrl) {
      u.searchParams.delete('join');
      const path = u.pathname.replace(/\/r\/[A-Za-z0-9-]+\/?/, '/');
      const qs = u.searchParams.toString();
      window.history.replaceState({}, '', path + (qs ? `?${qs}` : ''));
      const code = fromUrl.toUpperCase();
      stash(code);
      return code;
    }

    // No code in the URL — fall back to a still-valid stashed one (the return
    // leg from Stripe lands here with a bare URL).
    const raw = localStorage.getItem(PENDING_KEY);
    if (!raw) return null;
    const { code, ts } = JSON.parse(raw) as Pending;
    if (!code || typeof ts !== 'number' || Date.now() - ts > TTL_MS) {
      localStorage.removeItem(PENDING_KEY);
      return null;
    }
    return code;
  } catch {
    return null;
  }
}

/** Clear the pending join after an attempt, so a refresh won't re-fire it. */
export function clearPendingJoinCode(): void {
  try {
    localStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}
